import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { addTripItem, getTrip, updateTrip } from '../lifeos/api.js';
import { humanize, localInputToIso, stamp, tripDates, ymdInHomeZone } from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { options, text } from '../lifeos/forms.js';
import { errorText, useToast } from '../lifeos/toast.js';
import { Check } from '../lifeos/ui.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Group, Icon, IconTile, Loader, Page, Row, type IconName, Fact } from '../ui/index.js';
import type { PackingItem, Trip as TripT, TripItemKind } from '../lifeos/types.js';

const ITEM_KINDS: TripItemKind[] = ['flight', 'hotel', 'car', 'train', 'event', 'reservation', 'other'];

/** A booking's tile, by kind. */
const KIND_TILE: Record<string, [IconName, string]> = {
  flight: ['plane', '#0a84ff'],
  hotel: ['bed', '#5e5ce6'],
  car: ['package', '#ff9f0a'],
  train: ['clock', '#30b0c7'],
  event: ['calendar', '#ff375f'],
  reservation: ['utensils', '#ff9f0a'],
  other: ['circle', '#8e8e93'],
};

/**
 * One trip (`/trips/:id`): bookings in time order, then the packing list.
 * Packing ticks save immediately (they are a checklist, not a decision).
 */
export default function Trip(): JSX.Element {
  const { id = '' } = useParams();
  const trip = useLoad(() => getTrip(id), [id]);
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [packing, setPacking] = useState<PackingItem[] | null>(null);
  const [newItem, setNewItem] = useState('');
  const today = ymdInHomeZone(new Date());

  // A different trip: drop the local packing edits of the previous one.
  useEffect(() => setPacking(null), [id]);

  async function savePacking(t: TripT, next: PackingItem[]): Promise<void> {
    const before = packing ?? t.packing;
    setPacking(next);
    try {
      await updateTrip(t.id, { packing: next });
    } catch (err) {
      setPacking(before);
      toast.error(`Couldn't save the packing list: ${errorText(err)}`);
    }
  }

  const back = { to: '/trips', label: 'Trips' };
  if (!trip.data) {
    return (
      <Page title="" back={back}>
        <Loader state={trip}>{() => null}</Loader>
      </Page>
    );
  }
  const { trip: t, items } = trip.data;
  const list = packing ?? t.packing;
  const packed = list.filter((p) => p.packed).length;
  const sorted = [...items].sort((a, b) => (a.startsAt ?? '9999').localeCompare(b.startsAt ?? '9999'));

  return (
    <Page title={t.title} back={back} sub={[t.destination, tripDates(t, today)].filter(Boolean).join(' · ')}>
      <Group label="Who's going">{t.people.length > 0 && <Fact label="Going">{t.people.map(String).join(', ')}</Fact>}</Group>

      <Group header="Bookings" action={{ label: 'Add', onClick: () => setAdding(true) }}>
        {sorted.length === 0 ? (
          <Row tone="accent" leading={<Icon name="plus" size={20} />} title="Add a flight, stay or reservation" onClick={() => setAdding(true)} />
        ) : (
          sorted.map((it) => {
            const [icon, tint] = KIND_TILE[it.kind] ?? KIND_TILE.other!;
            return (
              <Row
                key={it.id}
                leading={<IconTile name={icon} color={tint} />}
                title={it.title}
                sub={
                  <>
                    {[it.startsAt ? stamp(it.startsAt) : null, it.provider, it.location].filter(Boolean).join(' · ') || humanize(it.kind)}
                  </>
                }
                detail={it.confirmation ? <span className="la-code la-conf">{it.confirmation}</span> : undefined}
              />
            );
          })
        )}
      </Group>

      <Group header={<>Packing{list.length > 0 && <span className="la-count num"> {packed}/{list.length}</span>}</>} label="Packing">
        {list.map((p, i) => (
          <Row
            key={`${p.item}:${i}`}
            className={`la-pack${p.packed ? ' la-pack--done' : ''}`}
            leading={
              <Check
                checked={p.packed}
                label={`${p.packed ? 'Unpack' : 'Pack'} ${p.item}`}
                onChange={() => void savePacking(t, list.map((x, j) => (j === i ? { ...x, packed: !x.packed } : x)))}
              />
            }
            title={p.item}
          />
        ))}
        <li className="ui-swipe la-addrow">
          <form
            className="la-addrow__form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              const item = newItem.trim();
              if (!item) return;
              setNewItem('');
              void savePacking(t, [...list, { item, packed: false }]);
            }}
          >
            <Icon name="plus" size={20} className="la-addrow__icon" />
            <input placeholder="Add something to pack" aria-label="Add something to pack" value={newItem} onChange={(e) => setNewItem(e.target.value)} />
            {newItem.trim() && (
              <button type="submit" className="la-addrow__save">
                Add
              </button>
            )}
          </form>
        </li>
      </Group>

      {t.notes && (
        <Group header="Notes">
          <Row className="la-prose" title={t.notes} />
        </Group>
      )}

      {adding && (
        <FormSheet
          title="Add a booking"
          submitLabel="Add"
          fields={[
            { key: 'title', label: 'Title', required: true, wide: true, placeholder: 'e.g. DL 1234 ROA → ATL' },
            { key: 'kind', label: 'Kind', kind: 'select', options: options(ITEM_KINDS, humanize) },
            { key: 'confirmation', label: 'Confirmation' },
            { key: 'startsAt', label: 'Starts', kind: 'datetime' },
            { key: 'endsAt', label: 'Ends', kind: 'datetime' },
            { key: 'provider', label: 'Provider' },
            { key: 'location', label: 'Where' },
          ]}
          initial={{ kind: 'flight' }}
          onClose={() => setAdding(false)}
          onSubmit={async (v) => {
            await addTripItem(t.id, {
              title: text(v.title)!,
              kind: v.kind as TripItemKind,
              confirmation: text(v.confirmation),
              startsAt: localInputToIso(v.startsAt ?? ''),
              endsAt: localInputToIso(v.endsAt ?? ''),
              provider: text(v.provider),
              location: text(v.location),
            });
            void trip.reload();
          }}
        />
      )}
    </Page>
  );
}
